import React from "react";
import { useTranslation } from "react-i18next";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; retry: () => void }>;
}

/**
 * 错误回退 UI 组件（函数组件，支持 i18n）
 */
function ErrorFallback({ error, retry }: { error?: Error; retry: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="min-h-64 flex items-center justify-center">
      <div className="text-center max-w-md">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {t("error.pageLoadFailed", "页面加载出错")}
        </h2>
        <p className="text-muted-foreground mb-4">
          {error?.message || t("error.unknownError", "发生了未知错误")}
        </p>
        <button
          onClick={retry}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded transition-colors"
        >
          {t("error.retry", "重试")}
        </button>
        <button
          onClick={() => window.location.reload()}
          className="ml-2 bg-muted hover:bg-muted/80 text-foreground px-4 py-2 rounded transition-colors"
        >
          {t("error.refresh", "刷新页面")}
        </button>
      </div>
    </div>
  );
}

/**
 * 错误边界组件
 * 捕获子组件中的 JavaScript 错误，并显示回退 UI
 */
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  retry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // 如果提供了自定义 fallback，使用它
      if (this.props.fallback) {
        const Fallback = this.props.fallback;
        return <Fallback error={this.state.error} retry={this.retry} />;
      }

      // 使用默认的国际化回退 UI
      return <ErrorFallback error={this.state.error} retry={this.retry} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
